'use client';

/**
 * Legacy C Adapter
 * ─────────────────────────────────────────────────────────────
 *
 * Bridges the old `C` color object (17 properties, statically exported from
 * lib/data.js) to the new ThemeProvider system.
 *
 * Why this exists:
 *   CoachApp.js has ~150 inline references to C.a, C.text, C.bd etc.
 *   Rewriting all of them is risky (regression-prone, tedious, error-prone).
 *   This adapter lets all existing code keep working while colors now come
 *   from the reactive theme store.
 *
 * Result: when the theme transitions, every C.* reference in CoachApp.js
 * re-renders with the new palette. No inline changes needed.
 *
 * Migration path:
 *   Step 1 (this file): Adapter in place, CoachApp.js unchanged apart from
 *                       using `useLegacyC()` in place of imported C.
 *   Step 2 (future):    Incrementally replace C.* usages with direct
 *                       useColors() calls as components are touched.
 *   Step 3 (future):    Remove adapter entirely, delete old C export.
 */

import { useColors } from './ThemeProvider';
import type { ColorPalette } from './colors';

/**
 * Shape of the old C object. Every property in the original lib/data.js C
 * object is mapped here for 1:1 backward compat.
 */
export interface LegacyColors {
  // Core
  bg: string;
  white: string;
  text: string;
  ts: string;        // text-secondary

  // Accent
  a: string;         // accent
  al: string;        // accent-light
  ad: string;        // accent-dark
  at: string;        // accent-text (contrast color on accent backgrounds — NEW in Phase 3.1)
  aink: string;      // accent als Vordergrund-Text/Icon (Dark: Lime, Light: Slate) — lesbar über Modi

  // Semantic
  g: string;         // green (success)
  gl: string;        // green-light
  b: string;         // blue (info)
  bl: string;        // blue-light
  o: string;         // orange (warning)
  ol: string;        // orange-light
  p: string;         // purple
  r: string;         // red (danger)

  // Structural
  bd: string;        // border
  cd: string;        // card
  sh: string;        // shadow
}

/**
 * Build legacy C shape from the modern palette.
 * Pure function — trivially testable.
 */
export function adaptColors(colors: ColorPalette): LegacyColors {
  return {
    // Core
    bg: colors.bg,
    white: colors.surface,        // old C.white was always #FFFFFF — map to current surface
    text: colors.text,
    ts: colors.textDim,

    // Accent
    a: colors.accent,
    al: colors.accentSoft,
    ad: colors.accent,            // old C.ad was darker accent; in flat design we collapse
    at: colors.accentText,        // NEW: use for text/icons on accent bg (white on dark, black on lime)
    aink: colors.accentInk,       // accent als Vordergrund-Text/Icon — lesbar in Dark (Lime) UND Light (Slate)

    // Semantic — stay semantic across themes (reds are red, etc.)
    g: colors.success,
    gl: colors.successSoft,
    b: colors.info,
    bl: colors.infoSoft,
    o: colors.warning,
    ol: colors.warningSoft,
    p: colors.purple,
    r: colors.danger,

    // Structural
    bd: colors.border,
    cd: colors.surface,
    sh: colors.shadow,
  };
}

/**
 * React hook: returns the legacy C object derived from the current theme.
 * Use this wherever the old `C` was imported from lib/data.js.
 */
export function useLegacyC(): LegacyColors {
  const colors = useColors();
  return adaptColors(colors);
}
